import { COMPLIMENTARY_STATUS, type Account, type Payment } from '@lyrmo/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import { cx } from '../../lib/cx';
import { useAccount } from './account-context';
import { AccountFrame } from './AccountFrame';
import { usageLevel } from './usage-level';
import './billing.css';

/** The latest payments, fetched from the payment provider each time: Lyrmo doesn't keep them. */
function PaymentHistory() {
  const { t, format, describeError } = useI18n();
  const { client } = useAccount();
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const headingId = useId();

  useEffect(() => {
    let current = true;
    client.listPayments().then(
      (loaded) => {
        if (current) setPayments(loaded);
      },
      (caught: unknown) => {
        if (current) setError(describeError(caught));
      },
    );
    return () => {
      current = false;
    };
  }, [client, describeError]);

  return (
    <section className="billing-history" aria-labelledby={headingId}>
      <h4 id={headingId}>{t.billing.history}</h4>
      {error ? (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      ) : !payments ? (
        <p className="billing-history-note" role="status">
          {t.billing.loadingPayments}
        </p>
      ) : payments.length === 0 ? (
        <p className="billing-history-note">{t.billing.noPayments}</p>
      ) : (
        <ul className="billing-history-list">
          {payments.map((payment) => {
            const date = format.fullDate(payment.billedAt);
            return (
              <li key={payment.id}>
                <span className="billing-history-date">{date}</span>
                <span className="billing-history-amount">
                  {format.price(payment.amount, payment.currency)}
                </span>
                <a
                  className="billing-invoice"
                  href={client.invoiceUrl(payment.id)}
                  target="_blank"
                  rel="noopener"
                  aria-label={t.billing.invoiceFor(date)}
                >
                  {t.billing.invoice}
                  <Icon name="external" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function BillingDetails({ account }: { readonly account: Account }) {
  const { t, format, describeError } = useI18n();
  const { client, openDialog, closeDialog, cancelSubscription, resumeSubscription } = useAccount();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<'cancel' | 'resume' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { plan, subscription, usage, user } = account;
  const current = subscription?.plan === plan ? subscription : null;
  const periodEnd = current?.currentPeriodEnd ?? null;
  const ending = current?.cancelAtPeriodEnd ?? false;
  const pastDue = current?.status === 'past_due';
  const trial = current?.status === 'trialing';
  // Given for free: there's no payment method to change, nor subscription to cancel.
  const complimentary = current?.status === COMPLIMENTARY_STATUS;
  const status = pastDue
    ? t.billing.status.pastDue
    : ending
      ? t.billing.status.ending
      : trial
        ? t.billing.status.trial
        : t.billing.status.active;
  const renewal = ending
    ? t.billing.accessUntil
    : trial
      ? t.billing.trialEnds
      : pastDue
        ? t.billing.periodEnds
        : t.billing.renewsOn;

  /** Asks before cancelling, or stops asking, keeping focus on the buttons that replace these. */
  function ask(asking: boolean) {
    flushSync(() => {
      setConfirming(asking);
      setError(null);
    });
    (asking ? keepRef : cancelRef).current?.focus();
  }

  // Either way, the dialog closes and a notice says what changed, as after changing plans.
  async function run(action: 'cancel' | 'resume') {
    setBusy(action);
    setError(null);
    try {
      await (action === 'cancel' ? cancelSubscription() : resumeSubscription());
      closeDialog();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <p className="dialog-intro">{t.billing.intro}</p>

      {pastDue && (
        <p className="billing-warning" role="alert">
          {t.billing.pastDueWarning}
        </p>
      )}

      <section className="billing-plan" aria-label={t.billing.subscription}>
        <div className="billing-plan-heading">
          <div className="billing-plan-name">
            <span className="billing-plan-icon">
              <Icon name="sparkle" />
            </span>
            <div>
              <p className="billing-eyebrow">{t.billing.currentPlan}</p>
              <h3>{t.planNames[plan]}</h3>
            </div>
          </div>
          <span className="billing-status" data-warning={pastDue || ending}>
            <span aria-hidden="true" />
            {status}
          </span>
        </div>
        <p className="billing-plan-description">
          {rich(t.billing.summary(usage.limit), {
            credits: <strong>{format.number(usage.limit)}</strong>,
          })}
        </p>
        <div className="billing-plan-bottom">
          {periodEnd && (
            <div className="billing-renewal">
              <Icon name="clock" />
              <span>{rich(renewal, { date: <strong>{format.date(periodEnd)}</strong> })}</span>
            </div>
          )}
          <Button
            className="billing-change-plan"
            onClick={() => {
              openDialog('plans', 'billing');
            }}
          >
            {t.billing.comparePlans}
          </Button>
        </div>
        {ending && <p className="billing-ending-note">{t.billing.endingNote}</p>}
      </section>

      <section
        className="billing-usage"
        data-level={usageLevel(usage)}
        aria-labelledby="billing-usage-heading"
      >
        <div className="billing-usage-heading">
          <h3 id="billing-usage-heading">{t.billing.todaysCredits}</h3>
          <p>
            {rich(t.billing.leftOf, {
              left: <strong>{format.number(usage.remaining)}</strong>,
              limit: format.number(usage.limit),
            })}
          </p>
        </div>
        <progress
          className="billing-usage-meter"
          value={Math.min(usage.remaining, usage.limit)}
          max={usage.limit || 1}
          aria-label={t.account.creditsLeft}
        />
        <p className="billing-usage-caption">{t.billing.resetsMidnight}</p>
      </section>

      <section className="billing-payments" aria-labelledby="billing-payments-heading">
        <div className="billing-payment-heading">
          <span className="billing-payment-icon">
            <Icon name="card" />
          </span>
          <div>
            <h3 id="billing-payments-heading">{t.billing.payments}</h3>
            <p>{t.billing.paymentsText}</p>
          </div>
        </div>
        {complimentary ? null : confirming ? (
          <div className="billing-confirm" role="group" aria-label={t.billing.cancel}>
            <p>
              {t.billing.cancelQuestion(t.planNames[plan], periodEnd && format.date(periodEnd))}
            </p>
            <div className="billing-actions">
              <Button
                ref={keepRef}
                disabled={busy !== null}
                onClick={() => {
                  ask(false);
                }}
              >
                {t.billing.keep}
              </Button>
              <Button variant="primary" disabled={busy !== null} onClick={() => void run('cancel')}>
                {busy === 'cancel' ? t.billing.cancelling : t.billing.cancel}
              </Button>
            </div>
          </div>
        ) : (
          <div className="billing-actions">
            {/* The payment provider's own page: card details never pass through Lyrmo. */}
            <a
              className={cx('button', pastDue ? 'button--primary' : 'button--ghost billing-action')}
              href={client.paymentMethodUrl()}
              target="_blank"
              rel="noopener"
            >
              <span className="button-label">{t.billing.updatePayment}</span>
              <Icon name="external" />
            </a>
            {ending ? (
              <Button variant="primary" disabled={busy !== null} onClick={() => void run('resume')}>
                {busy === 'resume' ? t.billing.resuming : t.billing.keep}
              </Button>
            ) : (
              <Button
                ref={cancelRef}
                className="billing-action"
                onClick={() => {
                  ask(true);
                }}
              >
                {t.billing.cancel}
              </Button>
            )}
          </div>
        )}
        {error && (
          <p className="dialog-error" role="alert">
            {error}
          </p>
        )}
        <PaymentHistory />
      </section>

      <footer className="billing-footer">
        <p>{rich(t.common.signedInAs, { email: <strong>{user?.email}</strong> })}</p>
        <Button onClick={closeDialog}>{t.common.done}</Button>
      </footer>
    </>
  );
}

export function BillingDialog() {
  const { t } = useI18n();
  const { account, dialog, closeDialog } = useAccount();
  return (
    <AccountFrame
      open={dialog === 'billing'}
      onClose={closeDialog}
      title={t.billing.title}
      className="dialog--billing"
    >
      {account && <BillingDetails account={account} />}
    </AccountFrame>
  );
}
